import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Drop, Mountains, Coin, Wrench } from "@phosphor-icons/react";
import { useLang } from "../i18n/LanguageContext";
import SEO from "../components/SEO";

const Industries = () => {
  const { t } = useLang();

  const items = [
    {
      key: "copper",
      title: t.home.copper,
      desc: t.home.copperDesc,
      img: "https://images.pexels.com/photos/2101140/pexels-photo-2101140.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400",
      icon: Mountains,
      points: [
        "Sulphidisers for oxidised ore bodies (NaHS, Na₂S)",
        "Xanthate collector programs (PAX, SIBX, SIPX)",
        "Frothers — MIBC, Pine Oil",
        "High-chrome grinding media for SAG/Ball mills",
      ],
    },
    {
      key: "cobalt",
      title: t.home.cobalt,
      desc: t.home.cobaltDesc,
      img: "https://images.pexels.com/photos/35497312/pexels-photo-35497312.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400",
      icon: Drop,
      points: [
        "Sulphuric acid for primary leach",
        "Sodium metabisulphite & sulphidisers",
        "Cobalt sulphate for downstream chemistry",
        "Flocculants & coagulants for thickener circuits",
      ],
    },
    {
      key: "gold",
      title: t.home.gold,
      desc: t.home.goldDesc,
      img: "https://images.unsplash.com/photo-1610375461246-83df859d849d?auto=format&fit=crop&w=1400&q=80",
      icon: Coin,
      points: [
        "Sodium cyanide for CIP/CIL leach",
        "Activated carbon (coconut shell grade)",
        "Lead nitrate for leach acceleration",
        "Anhydrous borax & fluxes for refining",
      ],
    },
    {
      key: "water",
      title: t.home.water,
      desc: t.home.waterDesc,
      img: "https://images.unsplash.com/photo-1611273426858-450d8e3c9fce?auto=format&fit=crop&w=1400&q=80",
      icon: Wrench,
      points: [
        "Anionic & cationic polyacrylamide flocculants",
        "Aluminium sulphate, ferric chloride coagulants",
        "Lime, soda ash and pH chemistry",
        "Calcium chloride for dust suppression",
      ],
    },
  ];

  return (
    <>
      <SEO
        title="Industries Served · Copper, Cobalt, Gold, Water"
        description="Masika Chem SARL serves copper, cobalt, gold and industrial water treatment operations across the DRC with engineered reagent programs and consumables."
        path="/industries"
      />

      {/* HERO */}
      <section className="bg-white border-b border-neutral-200" data-testid="industries-hero">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 pt-20 pb-16 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-7">
            <div className="inline-flex items-center gap-3 mb-4">
              <span className="w-10 h-px bg-[#C8102E]" />
              <span className="text-[11px] font-bold tracking-[0.3em] uppercase text-[#C8102E]">
                {t.industries.overline}
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight text-neutral-900">
              {t.industries.title}
            </h1>
          </div>
          <div className="md:col-span-5 flex items-end">
            <p className="text-lg text-neutral-600 leading-relaxed">{t.industries.sub}</p>
          </div>
        </div>
      </section>

      {/* INDUSTRY BLOCKS */}
      <section className="bg-white" data-testid="industries-list">
        {items.map((it, idx) => {
          const Icon = it.icon;
          const reverse = idx % 2 === 1;
          return (
            <div
              key={it.key}
              data-testid={`industry-block-${it.key}`}
              className={`grid grid-cols-1 md:grid-cols-12 ${idx % 2 === 0 ? "bg-[#F7F7F5]" : "bg-white"}`}
            >
              <div
                className={`md:col-span-6 relative min-h-[360px] md:min-h-[520px] ${reverse ? "md:order-2" : ""}`}
              >
                <img src={it.img} alt={it.title} className="absolute inset-0 w-full h-full object-cover" />
              </div>
              <div className="md:col-span-6 flex items-center px-5 lg:px-16 py-16">
                <div className="max-w-xl">
                  <Icon size={32} weight="duotone" className="text-[#C8102E] mb-6" />
                  <h2 className="text-3xl lg:text-4xl font-extrabold text-neutral-900 tracking-tight">
                    {it.title}
                  </h2>
                  <p className="mt-4 text-neutral-600 leading-relaxed">{it.desc}</p>
                  <ul className="mt-8 space-y-3">
                    {it.points.map((p, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-neutral-700">
                        <span className="mt-1.5 w-2 h-2 bg-[#E85A1F] shrink-0" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/products"
                    data-testid={`industry-cta-${it.key}`}
                    className="mt-10 inline-flex items-center gap-2 text-sm font-bold text-[#C8102E] hover:text-[#A00D25]"
                  >
                    {t.common.exploreProducts} <ArrowRight size={14} weight="bold" />
                  </Link>
                </div>
              </div>
            </div>
          );
        })}
      </section>
    </>
  );
};

export default Industries;
